import { Heading } from '@chakra-ui/layout'
import { Layout } from 'components/atoms'
import { InputBuild, SelectBuild } from 'components/molecules'
import { useContractContext } from 'contexts/contract'
import { AddressKeys } from 'interfaces/Address'
import { LocationsDTO } from 'models'
import { disableUfFields } from 'pages/Contracts/utils/disableUfFields'
import React, { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { getMunicipio } from 'services/municipio'
import { getCEP } from 'services/viacep'
import formatter from 'utils/formatter'
import { useDebounce } from 'utils/useDebounce'
import isDisableToEditContract from '../../StepsForm/Utils/isDisableToEditContract'
import { FormThirdGuarantorValue } from './form'
import { FormThirdGuarantorProps } from './interfaces'
import { FormThirdGuarantorKeys } from './types'

const FormThirdGuarantor: React.FC = () => {
    const {
        register,
        watch,
        formState: { errors },
        setValue,
        setError,
        trigger
    } = useFormContext<{ terceiroGarantidor: FormThirdGuarantorProps }>()

    const [searching, setSearching] = useState(false)
    const [location, setLocation] = useState<LocationsDTO[]>([])
    const [lastCep, setLastCep] = useState('')
    
    const form = useMemo(() => FormThirdGuarantorValue(location), [location])
    const { typeContractBoolean } = useContractContext()

    const hasError = (id: FormThirdGuarantorKeys) => {
        if (id.includes('endereco')) {
            return (
                errors.terceiroGarantidor?.endereco &&
                errors.terceiroGarantidor.endereco[id.split('.')[1] as AddressKeys]
            )
        } else {
            return (
                errors.terceiroGarantidor &&
                errors.terceiroGarantidor[
                id as Extract<
                    FormThirdGuarantorKeys,
                    'nome' | 'tipoDocumento' | 'documento' | 'telefone' | 'email'
                >
                ]
            )
        }
    }

    function hasCepChanged(cep: string, lastCep: string) {
        return lastCep && lastCep !== cep.replace(/\D/g, '')
    }

    function updateAddressFields({ bairro, logradouro, cep, lastCep }: any) {
        if (bairro) {
            setValue('terceiroGarantidor.endereco.bairro', bairro)
        } else if (hasCepChanged(cep, lastCep)) {
            setValue('terceiroGarantidor.endereco.bairro', '')
        }

        if (logradouro) {
            setValue('terceiroGarantidor.endereco.logradouro', logradouro)
        } else if (hasCepChanged(cep, lastCep)) {
            setValue('terceiroGarantidor.endereco.logradouro', '')
        }
    }

    const consultCEP = useDebounce(async (cep: string) => {
        cep.length === 8 &&
            (await getCEP(cep)
                .then(async response => {
                    const { cep, localidade, logradouro, bairro, uf } = response
                    const locales = await consultMunicipio(uf).then(data => data)

                    const local = `${formatter.clearCharacters(localidade).toUpperCase()}`

                    const municipio = `${
                        locales?.find(({ municipio }) => municipio === local)
                            ?.codigoTomDetran
                    }|${local}`

                    setSearching(false)

                    setValue('terceiroGarantidor.endereco', {
                        ...watch('terceiroGarantidor.endereco'),
                        cep,
                        uf,
                        municipio
                    })

                    updateAddressFields({ bairro, logradouro, cep, lastCep })
                    trigger('terceiroGarantidor.endereco')
                })
                .catch(() => {
                    setValue('terceiroGarantidor.endereco.municipio', '')
                    setValue('terceiroGarantidor.endereco.cep', '')
                    setError('terceiroGarantidor.endereco.cep', {
                        type: 'validate',
                        message: 'CEP Inválido'
                    })
                })
                .finally(() => {
                    setSearching(false)
                }))
    }, 300)

    const handleCEP = (cep: string) => {
        const value = cep === undefined ? '' : cep.replace(/\D/g, '')
        !typeContractBoolean && setSearching(value.length === 8)
        consultCEP(value)
    }

    const consultMunicipio = async (uf: string) => {
        if (uf) {
            setSearching(true)
            return await getMunicipio(uf)
                .then(({ data }) => {
                    setLocation(data)
                    return data
                })
                .finally(() => setSearching(false))
        } else {
            setLocation([])
        }
    }

    useEffect(() => {
        setValue('terceiroGarantidor.endereco.uf', '')
        setLastCep(watch('terceiroGarantidor.endereco.cep'))
        if (watch('terceiroGarantidor.endereco.cep')) {
            setSearching(true)
            handleCEP(watch('terceiroGarantidor.endereco.cep'))
        } else {
            handleCEP(watch('terceiroGarantidor.endereco.uf'))
        }
    }, [])

    return (
        <Layout
            type="container"
            px={8}
            full
            display="grid"
            gap={8}
            data-testid="form-container-terceiro-garantidor"
        >
            <Layout type="row" data-testid="form-heading">
                <Heading size="md" textAlign={{ sm: 'center' }}>
                    Dados do terceiro garantidor
                </Heading>
            </Layout>
            <Layout type="row" gutter={{ y: 1.5 }} data-testid="form-fields">
                {form.map(({ layout, input, select }, key) => (
                    <Layout {...layout} key={key}>
                        {select ? (
                            <SelectBuild
                                {...select}
                                err={hasError(select.id)}
                                {...register(`terceiroGarantidor.${select.id}`)}
                                isSearching={select.isSearching && searching}
                                value={watch(`terceiroGarantidor.${select.id}`) as string}
                                isDisable={disableUfFields(
                                    typeContractBoolean,
                                    select,
                                    'terceiroGarantidor'
                                )}
                            />
                        ) : (
                            input && (
                                <InputBuild
                                    {...input}
                                    err={hasError(input.id)}
                                    {...register(`terceiroGarantidor.${input.id}`)}
                                    value={watch(`terceiroGarantidor.${input.id}`) as string}
                                    isSearching={input.isSearching && searching}
                                    onKeyUp={e => {
                                        input.id === 'endereco.cep' &&
                                            handleCEP(e.currentTarget.value as string)
                                    }}
                                    isDisable={
                                        typeContractBoolean &&
                                        isDisableToEditContract[
                                            `terceiroGarantidor.${input.id}`
                                        ]
                                    }
                                />
                            )
                        )}
                    </Layout>
                ))}
            </Layout>
        </Layout>
    )
}

export default FormThirdGuarantor